/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import {
  CreateWorkspaceRequest,
  CreateWorkspaceResponse,
  EditWorkspaceRequest,
  GetWorkspacesQueryParams,
  Workspace,
  WorkspaceState
} from '../models/workspaces';
import { WorkspaceService } from '../services/workspace.service';

function generateMockWorkspaces(count: number): Workspace[] {
  const workspaces: Workspace[] = [];
  const currentDate = new Date().toISOString();

  for (let i = 1; i <= count; i++) {
    const isArchived = i % 3 === 0;
    const isDeleted = i % 10 === 0;
    const name = isArchived ? `Archived Project ${i}` : `Active Workspace ${i}`;
    const state = 'DRAFTING';

    const workspace: Workspace = {
      id: `wksp-${i.toString().padStart(3, '0')}`,
      name: name,
      description: `Description for workspace number ${i}. This is a dynamic test entry. This is a dynamic test entry. This is a dynamic test entry. This is a dynamic test entry. This is a dynamic test entry. This is a dynamic test entry. This is a dynamic test entry.`,
      members: [
        {
          cognito_id: '2821f370-90d1-7006-d8b6-bbe70d6545ec',
          id: '2821f370-90d1-7006-d8b6-bbe70d6545ec',
          invitation_status: 'active',
          invited_by: '2821f370-90d1-7006-d8b6-bbe70d6545ec',
          role: 'admin'
        }
      ],
      last_activity: currentDate,
      created_at: currentDate,
      updated_at: currentDate,
      created_by: `user-${i % 3 === 0 ? 'admin' : 'member'}`,
      state: state,
      is_archived: isArchived,
      is_deleted: isDeleted,
      template_id: `temp-101`,
      template_version: `1.0.0`,
      template_name: isArchived ? 'R&D Template' : 'Standard Project',
      ai_doc_info: 'Money'
    };

    workspaces.push(workspace);
  }

  return workspaces;
}

@Injectable()
export class MockWorkspaceService extends WorkspaceService implements WorkspaceService {
  private mockWorkspaces = generateMockWorkspaces(20);
  public override getWorkspaces(params?: GetWorkspacesQueryParams): Observable<Workspace[]> {
    let result = this.mockWorkspaces.filter(w => !w.is_archived && !w.is_deleted);

    if (params?.include_archived) {
      result = result.concat(this.mockWorkspaces.filter(w => w.is_archived));
    }
    if (params?.include_deleted) {
      result = result.concat(this.mockWorkspaces.filter(w => w.is_deleted));
    }

    return of(result).pipe(delay(2000));
  }

  public override createWorkspace(data: CreateWorkspaceRequest): Observable<CreateWorkspaceResponse> {
    const newId = `wksp-${Date.now()}`;
    const newWorkspace: Workspace = {
      ...data,
      id: newId,
      members: [
        {
          cognito_id: 'a3d4f822-f051-70db-6a6b-03cfc4b38065',
          id: 'a3d4f822-f051-70db-6a6b-03cfc4b38065',
          invitation_status: 'active',
          invited_by: 'a3d4f822-f051-70db-6a6b-03cfc4b38065',
          role: 'admin'
        }
      ],
      last_activity: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: 'mock-user',
      state: 'DRAFTING',
      is_archived: false,
      is_deleted: false,
      template_name: 'Mock Template'
    };
    this.mockWorkspaces.push(newWorkspace);

    return of({ id: newId }).pipe(delay(2000));
  }

  public override getWorkspaceDetails(workspaceId: string): Observable<Workspace> {
    const workspace = this.mockWorkspaces.find(w => w.id === workspaceId);

    if (workspace) {
      return of(workspace);
    }
    return of(this.mockWorkspaces[0] as Workspace).pipe(delay(2000));
  }

  public override editWorkspace(workspaceId: string, data: EditWorkspaceRequest): Observable<void> {
    const workspace = this.mockWorkspaces.find(w => w.id === workspaceId);
    if (workspace) {
      workspace.name = data.name;
      workspace.description = data.description;
      workspace.updated_at = new Date().toISOString();
    }
    return of(undefined).pipe(delay(2000));
  }

  public override archiveWorkspace(workspaceId: string): Observable<void> {
    const workspace = this.mockWorkspaces.find(w => w.id === workspaceId);
    if (workspace) {
      workspace.is_archived = true;
      workspace.updated_at = new Date().toISOString();
    }
    return of(undefined).pipe(delay(2000));
  }

  public override unarchiveWorkspace(workspaceId: string): Observable<void> {
    const workspace = this.mockWorkspaces.find(w => w.id === workspaceId);
    if (workspace) {
      workspace.is_archived = false;
      workspace.state = 'DRAFTING';
      workspace.updated_at = new Date().toISOString();
    }
    return of(undefined).pipe(delay(2000));
  }

  public override deleteWorkspace(workspaceId: string): Observable<void> {
    const index = this.mockWorkspaces.findIndex(w => w.id === workspaceId);
    if (index > -1) {
      this.mockWorkspaces[index].is_deleted = true;
    }
    return of(undefined).pipe(delay(2000));
  }

  public override submitForReview(_workspaceId: string) {
    return of({ message: '' }).pipe(delay(1500));
  }

  public override withdrawToDrafting(_workspaceId: string) {
    return of({ message: '' }).pipe(delay(1500));
  }

  public override approveWorkspace(_workspaceId: string) {
    return of({ message: '' }).pipe(delay(1500));
  }

  public override finalizeWorkspace(_workspaceId: string) {
    return of({ message: '' }).pipe(delay(1500));
  }
}
